import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    LayoutDashboard, 
    Users, 
    Wallet, 
    Receipt, 
    Map, 
    Calculator,
    BarChart3,
    Settings,
    LogOut,
    Building2,
    Menu,
    X,
    UserCog,
    Briefcase,
    DollarSign,
    PieChart,
    SlidersHorizontal,
    Navigation,
    FileText,
    Clock,
    Shield
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'supervisor', 'cajero', 'cobrador'] },
    { path: '/mi-ruta', icon: Navigation, label: 'Mi Ruta', roles: ['cobrador'] },
    { path: '/clientes', icon: Users, label: 'Clientes', roles: ['admin', 'supervisor', 'cajero', 'cobrador'] },
    { path: '/solicitudes', icon: FileText, label: 'Solicitudes', roles: ['admin', 'supervisor', 'cajero'] },
    { path: '/analisis', icon: BarChart3, label: 'Análisis', roles: ['admin', 'supervisor'] },
    { path: '/prestamos', icon: Wallet, label: 'Préstamos', roles: ['admin', 'supervisor', 'cajero', 'cobrador'] },
    { path: '/simulador', icon: SlidersHorizontal, label: 'Simulador', roles: ['admin', 'supervisor', 'cajero', 'cobrador'] },
    { path: '/pagos', icon: Receipt, label: 'Pagos', roles: ['admin', 'supervisor', 'cajero', 'cobrador'] },
    { path: '/rutas', icon: Map, label: 'Rutas', roles: ['admin', 'supervisor'] },
    { path: '/caja', icon: Calculator, label: 'Caja', roles: ['admin', 'supervisor', 'cajero', 'cobrador'] },
    { path: '/reportes', icon: BarChart3, label: 'Reportes', roles: ['admin', 'supervisor'] },
    { path: '/contabilidad', icon: PieChart, label: 'Contabilidad', roles: ['admin', 'supervisor'] },
    { path: '/empleados', icon: Briefcase, label: 'Empleados', roles: ['admin'] },
    { path: '/nomina', icon: DollarSign, label: 'Nómina', roles: ['admin'] },
    { path: '/usuarios', icon: UserCog, label: 'Usuarios', roles: ['admin'] },
    { path: '/empresa', icon: Building2, label: 'Empresa', roles: ['admin'] },
    { path: '/configuracion', icon: Settings, label: 'Configuración', roles: ['admin'] },
    { path: '/superadmin', icon: Shield, label: 'Super Admin', roles: ['superadmin'] },
];

export const Layout = ({ children }) => {
    const { user, logout, hasRole } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const diasRestantes = user?.tenant_dias_restantes;
    const planTrial = user?.tenant_plan === 'trial';
    const isSuperAdmin = user?.rol === 'superadmin';
    const showTrialBanner = planTrial && diasRestantes !== undefined && !isSuperAdmin;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const filteredNavItems = navItems.filter(item => hasRole(item.roles));

    const NavContent = () => (
        <>
            <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                        <h2 className="font-heading font-bold text-white">PrestaGest</h2>
                        <p className="text-xs text-white/60">Gestión de Préstamos</p>
                    </div>
                </div>
            </div>
            <ScrollArea className="flex-1 py-4">
                <nav className="space-y-1 px-3">
                    {filteredNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                                    isActive
                                        ? 'bg-primary text-primary-foreground'
                                        : 'text-white/70 hover:text-white hover:bg-white/10'
                                }`
                            }
                            data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
            </ScrollArea>
            <div className="p-4 border-t border-white/10">
                {showTrialBanner && (
                    <div data-testid="trial-banner-sidebar" className={`mb-3 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2 ${
                        diasRestantes <= 3 
                            ? 'bg-red-500/15 text-red-400 border border-red-500/30' 
                            : diasRestantes <= 7 
                                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                                : 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30'
                    }`}>
                        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>Trial: {diasRestantes} {diasRestantes === 1 ? 'día' : 'días'} restantes</span>
                    </div>
                )}
                <div className="flex items-center gap-3 px-3 py-2 mb-3">
                    <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-sm font-semibold text-white">
                            {user?.nombre?.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.nombre}</p>
                        <p className="text-xs text-white/60 capitalize">{user?.rol}</p>
                    </div>
                </div>
                <Button
                    variant="ghost"
                    className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                </Button>
            </div>
        </>
    );

    return (
        <div className="min-h-screen bg-background">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:w-64 lg:flex-col bg-secondary">
                <NavContent />
            </aside>

            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 bg-secondary flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                    <Building2 className="w-7 h-7 text-white" />
                    <span className="font-heading font-bold text-white">PrestaGest</span>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    className="text-white"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    data-testid="mobile-menu-btn"
                >
                    {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </Button>
            </header>

            {/* Mobile Sidebar Overlay */}
            {mobileOpen && (
                <div 
                    className="lg:hidden fixed inset-0 z-40 bg-black/50"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Mobile Sidebar */}
            <aside 
                className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-secondary transform transition-transform duration-300 ${
                    mobileOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <NavContent />
            </aside>

            {/* Main Content */}
            <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
                {showTrialBanner && (
                    <div data-testid="trial-banner-top" className={`px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 ${
                        diasRestantes <= 3 
                            ? 'bg-red-600 text-white' 
                            : diasRestantes <= 7 
                                ? 'bg-amber-600 text-white'
                                : 'bg-indigo-600 text-white'
                    }`}>
                        <Clock className="w-4 h-4" />
                        {diasRestantes <= 3 
                            ? `Quedan solo ${diasRestantes} ${diasRestantes === 1 ? 'día' : 'días'} de prueba. Contacte al 718-916-1401 para activar su plan.`
                            : `Período de prueba: ${diasRestantes} días restantes`
                        }
                    </div>
                )}
                <div className="p-4 md:p-6 lg:p-8">
                    {children}
                </div>
            </main>
        </div>
    );
};
